import React, { useState, useRef, useEffect } from "react";
import ResetPassword from "./ResetPassword";

const OTPVerification = ({ email, onVerifyOTP, onResendOTP, onBack }) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const inputRefs = useRef([]);

  // Timer countdown
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  // Focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError("");
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter" && otp.every((d) => d !== "")) handleVerify();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, "");
    if (pasteData.length <= 6) {
      const newOtp = pasteData.split("").concat(Array(6).fill("")).slice(0, 6);
      setOtp(newOtp);
      setError("");
      const nextIndex = Math.min(pasteData.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleVerify = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setError("Please enter the complete 6-digit OTP");
      return;
    }
    setIsLoading(true);
    setError("");
    setSuccess("");
    try {
      const result = await onVerifyOTP({ email, otp: otpString });
      if (result.success) {
        setSuccess("OTP verified successfully!");
        setOtpVerified(true);
      } else {
        setError(result.message || "Invalid OTP. Please try again.");
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch {
      setError("Verification failed. Please try again.");
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError("");
    setSuccess("");
    try {
      const result = await onResendOTP({ email });
      if (result.success) {
        setSuccess("New OTP sent to your email");
        setTimer(60);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        setError(result.message || "Failed to resend OTP");
      }
    } catch {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };


  if (otpVerified) {
    return (
      <div className="">
        <ResetPassword email={email} onBackToLogin={onBack} />
      </div>
    );
  }
  

  return (
    <div className="flex flex-col justify-center p-8 h-full w-full bg-white">
      <h2 className="text-3xl font-bold mb-2 text-black text-center">
        Verify OTP
      </h2>
      <p className="text-gray-600 text-sm mb-6 text-center">
        We've sent a 6-digit code to <span className="font-medium">{email}</span>
      </p>

      {error && <p className="text-red-600 mb-4 text-center font-medium">{error}</p>}
      {success && <p className="text-green-600 mb-4 text-center font-medium">{success}</p>}

      {/* OTP Inputs */}
      <div className="mb-6">
        <div className="flex gap-3 justify-center">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              className="w-12 h-12 text-center text-lg font-semibold border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              disabled={isLoading}
            />
          ))}
        </div>
      </div>

      <button
        onClick={handleVerify}
        disabled={isLoading || otp.some((d) => d === "")}
        className={`w-full py-3 rounded-lg text-white text-sm font-medium mb-4 ${
          isLoading || otp.some((d) => d === "")
            ? "bg-gray-300 cursor-not-allowed"
            : "bg-black hover:bg-gray-800"
        }`}
      >
        {isLoading ? "Verifying..." : "Verify OTP"}
      </button>

      {/* Resend OTP */}
      <div className="text-center mb-6">
        {!canResend ? (
          <p className="text-sm text-gray-500">
            Resend OTP in{" "}
            <span className="font-medium text-gray-700">{formatTime(timer)}</span>
          </p>
        ) : (
          <button
            onClick={handleResend}
            disabled={resendLoading}
            className="text-sm text-gray-800 hover:underline font-medium disabled:opacity-50"
          >
            {resendLoading ? "Sending..." : "Resend OTP"}
          </button>
        )}
      </div>

      <div className="text-center">
        <button
          onClick={onBack}
          disabled={isLoading}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          ← Back to forgot password
        </button>
      </div>
    </div>
  );
};

export default OTPVerification;
